import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AdministrationMenuViewModel } from '@modules/api/administration-menu-view-model.generated';
import { DashboardViewModel } from '@modules/api/dashboard-view-model.generated';
import { PowerBIViewModel } from '@modules/api/power-bi-view-model.generated';
import { CurrentRouteService } from '@modules/common/services/current-route.service';
import { GetCurrentUserDynamicMenusService } from '@modules/common/services/get-current-user-dynamic-menus.service';
import { PermissionService } from '@modules/common/services/permission.service';
import { NavSidebarGroupedMenuItem } from '@modules/navigation/models/nav-sidebar-grouped-menu-item';
import { NavSidebarMenu } from '@modules/navigation/models/nav-sidebar-menu';
import { NavSidebarMenuItem } from '@modules/navigation/models/nav-sidebar-menu-item';
import { filter, map, merge, Observable, ReplaySubject, switchMap } from 'rxjs';

const DASHBOARDS_TITLE = 'DASHBOARDS';
const REPORTING_TITLE = 'REPORTING';
const ANALYTICS_TITLE = 'ANALYTICS';
const ADMINISTRATION_TITLE = "ADMINISTRATION";

@Injectable({
    providedIn: 'root'
})
export class NavSidebarMenuService {
    private readonly _replaySubject = new ReplaySubject<NavSidebarMenu[]>(1);

    constructor(
        private _currentRouteService: CurrentRouteService,
        private _permissionService: PermissionService,
        private _getCurrentUserDynamicMenusService: GetCurrentUserDynamicMenusService,
        private readonly _router: Router
    ) {
        this.getMenuModel().subscribe(this._replaySubject);
    }

    public getMenus(): Observable<NavSidebarMenu[]> {
        return this._replaySubject;
    }

    private getMenuModel(): Observable<NavSidebarMenu[]> {
        return this.getPopulatedMenuData().pipe(map(menus =>
            menus.map(menu => {
                return <NavSidebarMenu>{
                    icon: menu.icon,
                    title: menu.title,
                    menuItems: menu.menuItems && menu.menuItems.length && menu.menuItems.map(item => <NavSidebarMenuItem>{
                        title: item.title,
                        url: '/' + item.url.join('/')
                    }),
                    groupedMenus: menu.groupedMenus?.map(groupedMenu => <NavSidebarGroupedMenuItem>{
                        header: groupedMenu.header,
                        menuItems: groupedMenu.menuItems?.map(item => <NavSidebarMenuItem>{
                            title: item.title,
                            url: '/' + item.url.join('/')
                        })
                    })
                
            }})
        ));
    }

    private getPopulatedMenuData(): Observable<FilterableMenu[]> {
        return this.permissionsOrRouteOrMenusChanged()
            .pipe(
                switchMap(() => this.getDynamicMenus()),
                map(dynamicMenus => {
                    const routeParams = this._currentRouteService.getCurrentRoute().params;
                    const menus = this.getRawMenuData().filter(menu =>
                        !menu.requiredParams || !menu.requiredParams.length || menu.requiredParams.every(p => hasParam(routeParams, p))
                    );

                    for (let menu of menus) {
                        menu.menuItems = menu.menuItems && menu.menuItems
                            .filter(item => !item.requiredParams || !item.requiredParams.length || item.requiredParams.every(p => hasParam(routeParams, p)))
                            .filter(item => !item.requiredPermissions || !item.requiredPermissions.length || item.requiredPermissions.some(p => this._permissionService.verifyPermission(p)));

                        if(menu.groupedMenus) {
                            for(let groupedItem of menu.groupedMenus) {
                                groupedItem.menuItems = groupedItem.menuItems && groupedItem.menuItems
                                    .filter(item => !item.requiredParams || !item.requiredParams.length || item.requiredParams.every(p => hasParam(routeParams, p)))
                                    .filter(item => !item.requiredPermissions || !item.requiredPermissions.length || item.requiredPermissions.some(p => this._permissionService.verifyPermission(p)));
                            }
                        }

                        if (menu.title == DASHBOARDS_TITLE) {
                            this.populateDynamicMenu('Dashboards', menu, dynamicMenus.dashboards);
                        }

                        if (menu.title == ADMINISTRATION_TITLE) {
                            this.addDynamicAdminMenuItemsToStaticMenu(menu, dynamicMenus.adminstrationMenus);
                        }

                        if (menu.menuItems) {
                            for (let menuItem of menu.menuItems) {
                                if (menuItem.url) {
                                    this.populateUrlParams(menuItem.url, routeParams);
                                }
                            }
                        }

                        if(menu.groupedMenus) {
                            for(let groupedItem of menu.groupedMenus) {
                                if (groupedItem.header == ANALYTICS_TITLE) {
                                    this.populateDynamicMenu('Analytics', groupedItem, dynamicMenus.powerBIReports);
                                }
            
                                if (groupedItem.header == REPORTING_TITLE) {
                                    this.addDynamicMenuItemsToStaticMenu('Analytics', groupedItem, dynamicMenus.powerBIPaginatedReports);
                                }
                                
                                for (let menuItem of groupedItem.menuItems!) {
                                    if (menuItem.url) {
                                        this.populateUrlParams(menuItem.url, routeParams);
                                    }
                                }
                            }
                            menu.groupedMenus = menu.groupedMenus.filter(x => x.menuItems!.length) 
                        }
                    }

                    return menus.filter(menu => menu.menuItems?.length || menu.groupedMenus?.some(groupedItem => groupedItem.menuItems?.length));
                })
            );
    }

    private permissionsOrRouteOrMenusChanged(): Observable<any> {
        return merge(
            this._permissionService.permissionsChanged,
            this._router.events.pipe(filter(event => event instanceof NavigationEnd)),
            this._getCurrentUserDynamicMenusService.dashboardsChanged(),
            this._getCurrentUserDynamicMenusService.powerBIReportsChanged(),
            this._getCurrentUserDynamicMenusService.powerBIPaginatedReportsChanged(),
            this._getCurrentUserDynamicMenusService.administrationMenusChanged()
        );
    }

    private async getDynamicMenus(): Promise<DynamicMenus> {
        return Promise.all([
            this._getCurrentUserDynamicMenusService.getDashboards(),
            this._getCurrentUserDynamicMenusService.getPowerBIReports(),
            this._getCurrentUserDynamicMenusService.getPowerBIPaginatedReports(),
            this._getCurrentUserDynamicMenusService.getAdministrationMenus(),
        ]).then<DynamicMenus>(models => ({
            dashboards: models[0],
            powerBIReports: models[1],
            powerBIPaginatedReports: models[2],
            adminstrationMenus: models[3],
        }));
    }

    private populateDynamicMenu(menuTitle: string, menu: FilterableMenu | FilterableGroupMenu, o: DashboardViewModel[] | PowerBIViewModel[]): void {
        menu.menuItems = o && o.map(x => {
            return {
                title: x.name!,
                url: [':environment', ':clientName', menuTitle, x.name!]
            }
        }) || [];
    }

    private addDynamicMenuItemsToStaticMenu(menuTitle: string, menu: FilterableGroupMenu, o: PowerBIViewModel[]): void {
        const paginatedReportMenuItems: FilterableMenuItem[] = o.map(x => {
            return {
                title: x.name!,
                url: [':environment', ':clientName', menuTitle, x.name!]
            }
        });

        menu.menuItems = menu.menuItems?.concat(paginatedReportMenuItems).sort((a, b) => a.title.localeCompare(b.title))
    }

    private addDynamicAdminMenuItemsToStaticMenu(menu: FilterableMenu | FilterableGroupMenu, administrationMenus: AdministrationMenuViewModel[]): void {
        const adminMenuItems: FilterableMenuItem[] = administrationMenus.map(x => ({
                title: x.friendlyName || x.name!,
                url: [':environment', ':clientName', x.baseUrlSegment!, x.name!]
        }));

        menu.menuItems = menu.menuItems!.concat(adminMenuItems).sort((a, b) => a.title.localeCompare(b.title));
    }

    private populateUrlParams(url: string[], routeParams: { [key: string]: any }): void {
        for (let i = url.length - 1; i >= 0; i--) {
            if (url[i].startsWith(':')) {
                let routeParam = routeParams[url[i].substr(1)];
                if (routeParam) {
                    url[i] = routeParam;
                } else {
                    url.splice(i, 1);
                }
            }
        }
    }

    private getRawMenuData(): FilterableMenu[] {
        return [
            {
                icon: 'fa-desktop',
                title: 'DASHBOARDS',
                menuItems: []
            },
            {
                icon: 'fa-circle-user',
                title: 'PARTICIPANT',
                groupedMenus: [
                    {
                        header: 'SEARCH',
                        menuItems: [
                            {
                                title: 'Search',
                                url: [':environment', ':clientName', 'Participant/Search'],
                                requiredPermissions: ['View Participant Profile']
                            }
                        ]
                    },
                    {
                        header: 'PROFILE',
                        menuItems: [
                            {
                                title: 'Profile',
                                url: [':environment', ':clientName', 'Participant', ':participantId'],
                                requiredParams: [':participantId'],
                                requiredPermissions: ['View Participant Profile']
                            },
                            {
                                title: 'Benefits',
                                url: [':environment', ':clientName', 'Participant', ':participantId', 'Benefits'],
                                requiredParams: [':participantId'],
                                requiredPermissions: ['View Participant Profile']
                            },
                            {
                                title: 'Dependents & Beneficiaries',
                                url: [':environment', ':clientName', 'Participant', ':participantId', 'Dependents'],
                                requiredParams: [':participantId'],
                                requiredPermissions: ['View Participant Profile']
                            },
                            {
                                title: 'Notes',
                                url: [':environment', ':clientName', 'Participant', ':participantId', 'Notes'],
                                requiredParams: [':participantId'],
                                requiredPermissions: ['View Participant Profile']
                            },
                            {
                                title: 'Documents',
                                url: [':environment', ':clientName', 'Participant', ':participantId', 'Documents'],
                                requiredParams: [':participantId'],
                                requiredPermissions: ['View Participant Profile']
                            },
                            {
                                title: 'Deductions',
                                url: [':environment', ':clientName', 'Participant', ':participantId', 'Deductions'],
                                requiredParams: [':participantId'],
                                requiredPermissions: ['View Participant Deduction List']
                            },
                            {
                                title: 'Instruction History',
                                url: [':environment', ':clientName', 'Participant', ':participantId', 'Instructions'],
                                requiredParams: [':participantId'],
                                requiredPermissions: ['View Participant Instruction History']
                            },
                        ]
                    }
                ]
            },
            {
                icon: 'fa-briefcase',
                title: ADMINISTRATION_TITLE,
                menuItems: [
                    {
                        title: 'Census Load Reports',
                        url: [':environment', ':clientName', 'CensusReports/Dashboard'],
                        requiredPermissions: ['View Census Reports']
                    },
                    {
                        title: 'Data Integrity Errors',
                        url: [':environment', ':clientName', 'DataIntegrity/DataIntegrityErrors'],
                        requiredPermissions: ['View Data Integrity Errors']
                    },
                    {
                        title: 'Issue Tracker',
                        url: [':environment', ':clientName', 'IssueTracker/SSO']
                    },
                    {
                        title: 'Outstanding Enrollments',
                        url: [':environment', ':clientName', 'Administration/ParticipantTransactions'],
                        requiredPermissions: ['View Participant Transactions']
                    },
                    {
                        title: 'Verifications',
                        url: [':environment', ':clientName', 'DependentVerification/Verifications'],
                        requiredPermissions: ['Edit Dependent Verification']
                    },
                ]
            },
            {
                icon: 'fa-file-pen',
                title: 'REPORTINGS & ANALYTICS',
                groupedMenus: [
                    {
                        header: 'REPORTING',
                        menuItems: [
                            {
                                title: 'Ad Hoc Reports',
                                url: [':environment', ':clientName', 'Reporting/AdHocReports'],
                                requiredPermissions: ['View Ad Hoc Reports']
                            },
                            {
                                title: 'Standard Reports',
                                url: [':environment', ':clientName', 'Reporting/Reports'],
                                requiredPermissions: ['View Standard Reports']
                            },
                        ]
                    },
                    {
                        header: 'ANALYTICS',
                        menuItems: []
                    }
                ]
            },
            {
                icon: 'fa-cog',
                title: 'CONFIGURATION',
                groupedMenus: [
                    {
                        header: 'LOGIN',
                        menuItems: [
                            {
                                title: 'Login Personal Identifiable Information',
                                url: [':environment', ':clientName', 'AdminPortalConfig/PersonalInfo'],
                                requiredPermissions: ['View Login PII']
                            },
                            {
                                title: 'Login Styling',
                                url: [':environment', ':clientName', 'PortalConfig/LoginStyling'],
                                requiredPermissions: ['Edit Login Styling']
                            },
                            {
                                title: 'Sign Up & Password Reset Emails',
                                url: [':environment', ':clientName', 'AdminPortalConfig/SignUpAndPasswordResetEmails'],
                                requiredPermissions: ['View Case Email Configuration']
                            },
                        ]
                    },
                    {
                        header: 'PORTAL',
                        menuItems: [
                            {
                                title: 'Available Benefits',
                                url: [':environment', ':clientName', 'PortalConfig/AvailableBenefits'],
                                requiredPermissions: ['View Available Benefits']
                            },
                            {
                                title: 'FAQs',
                                url: [':environment', ':clientName', 'PortalConfig/FAQ'],
                                requiredPermissions: ['View FAQs']
                            },
                            {
                                title: 'Forms',
                                url: [':environment', ':clientName', 'PortalConfig/Forms'],
                                requiredPermissions: ['View Forms']
                            },
                            {
                                title: 'Mobile App Information',
                                url: [':environment', ':clientName', 'PortalConfig/MobileAppInfo'],
                                requiredPermissions: ['Edit Mobile App Info']
                            },
                            {
                                title: 'Navigation',
                                url: [':environment', ':clientName', 'TitaniaNavigation/Navigation'],
                                requiredPermissions: ['View Titania Menus']
                            },
                            {
                                title: 'Plan Details',
                                url: [':environment', ':clientName', 'PortalConfig/PlanDetails'],
                                requiredPermissions: ['View Plan Details']
                            }
                        ]
                    },
                    {
                        header: 'ENROLLMENT BUILDER',
                        menuItems: [
                            {
                                title: 'Enrollments',
                                url: [':environment', ':clientName', 'EnrollmentBuilder/Enrollments'],
                                requiredPermissions: ['View Transactions']
                            },
                            {
                                title: 'Evidence of Insurability Text',
                                url: [':environment', ':clientName', 'EnrollmentBuilder/EOITextConfig'],
                                requiredPermissions: ['View Transactions']
                            }
                        ]
                    },
                    {
                        header: 'BENEFIT PLANS',
                        menuItems: [
                            {
                                title: 'Application Questions',
                                url: [':environment', ':clientName', 'ApplicationQuestions'],
                                requiredPermissions: ['View Case Application Questions']
                            },
                            {
                                title: 'Benefit Plan Documents',
                                url: [':environment', ':clientName', 'BenefitPlanDocuments'],
                                requiredPermissions: ['View Benefit Plan Documents']
                            },
                            {
                                title: 'Case Plan Groups',
                                url: [':environment', ':clientName', 'CasePlanGroups'],
                                requiredPermissions: ['View Benefit Plan Groups']
                            },
                            {
                                title: 'Global Credit Configuration',
                                url: [':environment', ':clientName', 'CaseConfig/GlobalCreditConfiguration'],
                                requiredPermissions: ['View Global Credit Configuration']
                            },
                        ]
                    },
                    {
                        header: 'CASE LEVEL CONFIGURATION',
                        menuItems: [
                            {
                                title: 'Administration Portal Styling',
                                url: [':environment', ':clientName', 'AdminPortalConfig/Styling'],
                                requiredPermissions: ['Edit Admin Portal Styling']
                            },
                            {
                                title: 'Case Report',
                                url: [':environment', ':clientName', 'Reporting/CaseReport'],
                                requiredPermissions: ['View Case Report Config']
                            },
                            {
                                title: 'Data Integrity Exclusions',
                                url: [':environment', ':clientName', 'DataIntegrity/Exclusions'],
                                requiredPermissions: ['View Data Integrity Exclusions']
                            },
                            {
                                title: 'Dependent Verification Rules',
                                url: [':environment', ':clientName', 'DependentVerificationRules/DependentVerificationRules'],
                                requiredPermissions: ['View Dependent Verification Rules']
                            },
                            {
                                title: 'Eligibility Windows',
                                url: [':environment', ':clientName', 'EligibilityWindows'],
                                requiredPermissions: ['View Eligibility Windows']
                            },
                            {
                                title: 'Enrollment Commit Rules',
                                url: [':environment', ':clientName', 'AutoCommitCancelTransaction/EnrollmentCommitRules'],
                                requiredPermissions: ['View Auto Commit Cancel Transaction Configuration']
                            },
                            {
                                title: 'Tier Structures',
                                url: [':environment', ':clientName', 'TierStructure/CaseLevel/TierStructures'],
                                requiredPermissions: ['View Case Tier Structures']
                            }
                        ]
                    },
                    {
                        header: 'COMMUNICATION',
                        menuItems: [
                            {
                                title: 'Automated Emails',
                                url: [':environment', ':clientName', 'AutomatedCommunications/AutomatedEmails'],
                                requiredPermissions: ['View Automated Email Configuration']
                            },
                            {
                                title: 'Email Configuration',
                                url: [':environment', ':clientName', 'AdminPortalConfig/EmailConfiguration'],
                                requiredPermissions: ['View Default Email Configuration']
                            },
                        ]
                    },
                    {
                        header: 'SYSTEM SETTINGS',
                        menuItems: [
                            {
                                title: 'Ad Hoc Reports Editor',
                                url: [':environment', ':clientName', 'Reporting/AdHocReportEditor'],
                                requiredPermissions: ['Edit Ad Hoc Reports']
                            },
                            {
                                title: 'Analytics Configuration',
                                url: [':environment', ':clientName', 'Analytics', 'Configuration'],
                                requiredPermissions: ['View Power BI Report Configuration']
                            },
                            {
                                title: 'Dashboard Editor',
                                url: [':environment', ':clientName', 'DashboardConfiguration/DashboardEditor'],
                                requiredPermissions: ['Edit Dashboards']
                            },
                            {
                                title: 'Data Integrity Primary Fixers',
                                url: [':environment', ':clientName', 'DataIntegrity/PrimaryFixers'],
                                requiredPermissions: ['View Primary Fixers']
                            },
                            {
                                title: 'Data Integrity Validator Configuration',
                                url: [':environment', ':clientName', 'DataIntegrity/ValidatorConfiguration'],
                                requiredPermissions: ['View Data Integrity Validator Info']
                            },
                            {
                                title: 'Enrollment Sub Step Types',
                                url: [':environment', ':clientName', 'EnrollmentBuilder/SubStepTypes'],
                                requiredPermissions: ['View Transactions']
                            },
                            {
                                title: 'Standard Reports Editor',
                                url: [':environment', ':clientName', 'Reporting/ReportEditor'],
                                requiredPermissions: ['Edit Standard Reports']
                            },
                            {
                                title: 'Tier Structures',
                                url: [':environment', ':clientName', 'TierStructure/Global/TierStructures'],
                                requiredPermissions: ['View Global Tier Structures']
                            },
                            {
                                title: 'Video Configuration',
                                url: [':environment', ':clientName', 'VideoConfiguration'],
                                requiredPermissions: ['View Global Videos']
                            }
                        ]
                    }
                ]
            },
            {
                icon: 'fa-screwdriver-wrench',
                title: 'TOOLS',
                groupedMenus: [
                    {
                        header: 'CENSUS',
                        menuItems: [
                            {
                                title: 'Census List',
                                url: [':environment', ':clientName', 'Census/List'],
                                requiredPermissions: ['View Census']
                            }
                        ]
                    },
                    {
                        header: 'FILES',
                        menuItems: [
                            {
                                title: 'Case Remittance File Configuration',
                                url: [':environment', ':clientName', 'RemittanceCaseFileDefinitionGroups'],
                                requiredPermissions: ["View Case Remit File Configuration"]
                            },
                            {
                                title: 'Deduction Code Setup',
                                url: [':environment', ':clientName', 'DeductionCodes'],
                                requiredPermissions: ['View Deduction Codes']
                            },
                            {
                                title: 'Instruction File Configuration',
                                url: [':environment', ':clientName', 'InstructionCaseConfig'],
                                requiredPermissions: ["View Instruction File Configuration"]
                            },
                            {
                                title: 'Instruction File Setups',
                                url: [':environment', ':clientName', 'InstructionFileSetup'],
                                requiredPermissions: ["View Instruction File Setup"]
                            },
                            {
                                title: 'Payment File Configuration',
                                url: [':environment', ':clientName', 'PaymentFileConfiguration'],
                                requiredPermissions: ['View Payment File Configuration']
                            },
                            {
                                title: 'Remittance File Carrier Groups',
                                url: [':environment', ':clientName', 'RemittanceFileCarrierGroups'],
                                requiredPermissions: ['View Remit File Carrier Groups']
                            },
                            {
                                title: 'Remittance File Configuration',
                                url: [':environment', ':clientName', 'RemittanceFileDefinitionGroups'],
                                requiredPermissions: ["View Remit File Configuration"]
                            }
                        ]
                    },
                    {
                        header: 'RULES',
                        menuItems: [
                            {
                                title: 'New Rule',
                                url: [':environment', ':clientName', 'ExpressionRules/Edit'],
                                requiredPermissions: ['View Rules']
                            },
                            {
                                title: 'Search',
                                url: [':environment', ':clientName', 'ExpressionRules/Search'],
                                requiredPermissions: ['View Rules']
                            },
                        ]
                    },
                    {
                        header: 'PUBLISH CENTER',
                        menuItems: [
                            {
                                title: 'Publish',
                                url: [':environment', ':clientName', 'PublishingCenter/Publish'],
                                requiredPermissions: ['Run Publish']
                            },
                            {
                                title: 'Publish History',
                                url: [':environment', ':clientName', 'PublishingCenter/PublishHistory'],
                                requiredPermissions: ['View Publish']
                            }
                        ]
                    },
                    {
                        header: 'DATA REFRESH',
                        menuItems: [
                            {
                                title: 'History',
                                url: [':environment', ':clientName', 'DataRefresh/RefreshDataHistory'],
                                requiredPermissions: ['View Data Refresh']
                            },
                            {
                                title: 'Queue',
                                url: [':environment', ':clientName', 'DataRefresh/RefreshDataQueue'],
                                requiredPermissions: ['View Data Refresh Queue']
                            },
                            {
                                title: 'Refresh',
                                url: [':environment', ':clientName', 'DataRefresh/RefreshData'],
                                requiredPermissions: ['Run Data Refresh']
                            },
                        ]
                    },
                    {
                        header: 'UTILITIES',
                        menuItems: [
                            {
                                title: 'Bulk Confirmation Statements',
                                url: [':environment', ':clientName', 'ConfStmtGen'],
                                requiredPermissions: ['View Bulk Confirmation Statement Generations']
                            },
                            {
                                title: 'Bulk Document Uploader',
                                url: [':environment', ':clientName', 'Files', 'Loader'],
                                requiredPermissions: ["View Upload Batch Files"]
                            },
                            {
                                title: 'Rollovers',
                                url: [':environment', ':clientName', 'Rollover'],
                                requiredPermissions: ['View Rollovers']
                            },
                        ]
                    }
                ]
            },
            {
                icon: 'fa-shield-halved',
                title: 'SECURITY',
                menuItems: [
                    {
                        title: "Environments",
                        url: [':environment', ':clientName', 'Environments'],
                        requiredPermissions: ["View Environments"]
                    },
                    {
                        title: 'Filter Sets',
                        url: [':environment', ':clientName', 'Users/UserEntityFilterConfigurationList'],
                        requiredPermissions: ['View Filter Sets']
                    },
                    {
                        title: 'SSO Incoming',
                        url: [':environment', ':clientName', 'CaseSSO'],
                        requiredPermissions: ['View Case SSO']
                    },
                    {
                        title: 'SSO Outgoing',
                        url: [':environment', ':clientName', 'CaseSSOServiceProviders'],
                        requiredPermissions: ['View Case SSO Service Providers']
                    },
                    {
                        title: 'Permissions',
                        url: [':environment', ':clientName', 'Permissions/Tree'],
                        requiredPermissions: ['View Permissions']
                    },
                    {
                        title: 'Permission Sets',
                        url: [':environment', ':clientName', 'Permissions/PermissionSets'],
                        requiredPermissions: ['View Permission Sets']
                    },
                    {
                        title: 'Publish Group',
                        url: [':environment', ':clientName', 'PublishingCenter/PublishGroups'],
                        requiredPermissions: ['View Publish Groups']
                    },
                    {
                        title: 'Users',
                        url: [':environment', ':clientName', 'Users/List'],
                        requiredPermissions: ['View External Users', 'View Internal Users']
                    },
                ]
            },
        ];
    }
}

interface FilterableMenu {
    icon: string;
    title: string;
    requiredParams?: string[];
    menuItems?: FilterableMenuItem[];
    groupedMenus?: FilterableGroupMenu[];
}

interface FilterableMenuItem {
    title: string;
    url: string[];
    requiredParams?: string[];
    requiredPermissions?: string[];
}

interface FilterableGroupMenu {
    header: string;
    requiredParams?: string[];
    menuItems?: FilterableMenuItem[];
}

interface DynamicMenus {
    dashboards: DashboardViewModel[];
    powerBIReports: PowerBIViewModel[];
    powerBIPaginatedReports: PowerBIViewModel[];
    adminstrationMenus: AdministrationMenuViewModel[];
}

function hasParam(routeParams: { [key: string]: any }, param: string): boolean {
    return !!routeParams[param.substr(1)];
}
